/**
 * Relative timestamps, with the exact moment kept alongside.
 *
 * Two surfaces need the same cascade and were about to hold two copies of it:
 * `RelativeTimeCell` in the table, and the question preview panel's version
 * list. A second copy is how "3 weeks ago" in one place becomes "21 days ago"
 * in another for the same row.
 *
 * **Relative is the visible form, absolute is the backup.** "1 year ago"
 * answers the question a reader actually has, which is whether this is recent,
 * and it answers it without arithmetic. But relative is lossy, and an author
 * deciding whether a question predates a curriculum change needs the date, so
 * every caller pairs the relative label with the absolute in a `Tip`.
 */

const RELATIVE_FMT = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" })

const ABS_FMT = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
})

/** Absolute without a clock, for dates where the hour is noise. */
const ABS_DATE_FMT = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" })

export interface RelativeTime {
  /** "1 year ago", "yesterday", "in 3 days". */
  relative: string
  /**
   * "2mth ago", "4d ago", "just now".
   *
   * For meta lines that sit beside other facts rather than standing alone. At
   * rail width "2 months ago" is most of a line on its own, so a version number
   * and a date could not share one; abbreviated they fit with room to spare,
   * and the exact date is a `Tip` away either way.
   */
  compact: string
  /** "Apr 18, 2026, 2:41 PM". */
  absolute: string
  /** "Apr 18, 2026". */
  absoluteDate: string
}

/** Abbreviations, not the CLDR "mo."/"yr." set, so months never read as minutes. */
const COMPACT_SUFFIX: Record<string, string> = {
  second: "s",
  minute: "m",
  hour: "h",
  day: "d",
  week: "w",
  month: "mth",
  year: "yr",
}

function compactLabel(
  value: number,
  unit: Intl.RelativeTimeFormatUnit
): string {
  const magnitude = Math.abs(value)
  // Under a minute there is no number worth printing, and "0s ago" is worse
  // than useless: it looks like a bug.
  if (unit === "second" && magnitude < 45) return "just now"

  const label = `${magnitude}${COMPACT_SUFFIX[unit] ?? ""}`
  return value < 0 ? `${label} ago` : `in ${label}`
}

/**
 * `null` for anything unparseable, so callers render an empty state rather
 * than the string "Invalid Date".
 *
 * @param now Overridable so snapshots and tests do not drift as time passes.
 */
/** Date-only ISO is stored as UTC midnight and reads hours ago in US/Asia. Same local day → now. */
function parseIsoInstant(iso: string, now: number): Date | null {
  const trimmed = iso.trim()
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
  if (dateOnly) {
    const y = Number(dateOnly[1])
    const m = Number(dateOnly[2]) - 1
    const d = Number(dateOnly[3])
    const today = new Date(now)
    if (
      today.getFullYear() === y &&
      today.getMonth() === m &&
      today.getDate() === d
    ) {
      return new Date(now)
    }
    const local = new Date(y, m, d, 12, 0, 0, 0)
    return Number.isNaN(local.getTime()) ? null : local
  }
  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function relativeTime(
  iso: string | null | undefined,
  now: number = Date.now()
): RelativeTime | null {
  if (!iso) return null
  const date = parseIsoInstant(iso, now)
  if (!date) return null

  const diffSec = Math.round((date.getTime() - now) / 1000)
  const abs = Math.abs(diffSec)

  let unit: Intl.RelativeTimeFormatUnit
  let value: number
  if (abs < 60) {
    unit = "second"
    value = diffSec
  } else if (abs < 3600) {
    unit = "minute"
    value = Math.round(diffSec / 60)
  } else if (abs < 86400) {
    unit = "hour"
    value = Math.round(diffSec / 3600)
  } else if (abs < 86400 * 7) {
    unit = "day"
    value = Math.round(diffSec / 86400)
  } else if (abs < 86400 * 30) {
    unit = "week"
    value = Math.round(diffSec / (86400 * 7))
  } else if (abs < 86400 * 365) {
    unit = "month"
    value = Math.round(diffSec / (86400 * 30))
  } else {
    unit = "year"
    value = Math.round(diffSec / (86400 * 365))
  }

  return {
    relative: RELATIVE_FMT.format(value, unit),
    compact: compactLabel(value, unit),
    absolute: ABS_FMT.format(date),
    absoluteDate: ABS_DATE_FMT.format(date),
  }
}
